import {
  CourseAttributesUpdate,
  CourseEditableBooleanAttributeKey,
  CourseEditableDropdownAttributeKey,
  UpdateCourseAttributesRequest,
} from '../interfaces/CoursesInterface';
import { CourseMappingAttributes, CourseMappingGroup } from '../interfaces/CourseMappingInterface';

export type CourseMappingCheckboxAttributeKey = CourseEditableBooleanAttributeKey;
export type CourseMappingDropdownAttributeKey = CourseEditableDropdownAttributeKey;
export type CourseMappingCreditsAttributeKey = Extract<keyof CourseAttributesUpdate, 'number_of_credits'>;

export type CourseMappingPendingAttributeKey =
  | CourseMappingCheckboxAttributeKey
  | CourseMappingDropdownAttributeKey
  | CourseMappingCreditsAttributeKey;

export type CourseMappingPendingAttributeValue<K extends CourseMappingPendingAttributeKey> = Exclude<
  CourseAttributesUpdate[K],
  undefined
>;

export type CourseMappingPendingChanges = Record<string, CourseAttributesUpdate>;

export const CHECKBOX_COLUMN_TO_ATTRIBUTE: Record<string, CourseMappingCheckboxAttributeKey> = {
  isCollegePreparatory: 'is_college_preparatory',
  isHonors: 'is_honors',
  isAp: 'is_ap',
  isIb: 'is_ib',
  isAice: 'is_aice',
  isDualEnrollment: 'is_dual_enrollment',
  isCte: 'is_cte',
  isLabScience: 'is_lab_science',
};

export const DROPDOWN_COLUMN_TO_ATTRIBUTE: Record<string, CourseMappingDropdownAttributeKey> = {
  subjectArea: 'subject_area',
  subjectSubcategory: 'subject_area_subcategory',
  languageCategory: 'language_category',
  languageLevel: 'language_level',
};

export const CREDITS_COLUMN_TO_ATTRIBUTE: Record<string, CourseMappingCreditsAttributeKey> = {
  numberOfCredits: 'number_of_credits',
};

export const getCheckboxAttributeKey = (columnKey: string): CourseMappingCheckboxAttributeKey | undefined =>
  CHECKBOX_COLUMN_TO_ATTRIBUTE[columnKey];

export const getDropdownAttributeKey = (columnKey: string): CourseMappingDropdownAttributeKey | undefined =>
  DROPDOWN_COLUMN_TO_ATTRIBUTE[columnKey];

export const getCreditsAttributeKey = (columnKey: string): CourseMappingCreditsAttributeKey | undefined =>
  CREDITS_COLUMN_TO_ATTRIBUTE[columnKey];

const valuesAreEqual = (left: unknown, right: unknown): boolean => {
  if (left == null && right == null) {
    return true;
  }

  return left === right;
};

export const getOriginalAttributeValue = <K extends CourseMappingPendingAttributeKey>(
  group: CourseMappingGroup,
  attributeKey: K
): CourseMappingPendingAttributeValue<K> | null => {
  const { attributes } = group;

  switch (attributeKey) {
    case 'subject_area':
      return (attributes.subject_area?.name ?? null) as CourseMappingPendingAttributeValue<K> | null;
    case 'subject_area_subcategory':
      return (attributes.subject_area?.subcategory ?? null) as CourseMappingPendingAttributeValue<K> | null;
    case 'language_category':
      return (attributes.language_category ?? null) as CourseMappingPendingAttributeValue<K> | null;
    case 'language_level':
      return (attributes.language_level ?? null) as CourseMappingPendingAttributeValue<K> | null;
    case 'number_of_credits':
      return (attributes.number_of_credits ?? null) as CourseMappingPendingAttributeValue<K> | null;
    default: {
      const booleanKey = attributeKey as CourseMappingCheckboxAttributeKey;
      return (attributes[booleanKey] ?? null) as CourseMappingPendingAttributeValue<K> | null;
    }
  }
};

export const getEffectiveAttributeValue = <K extends CourseMappingPendingAttributeKey>(
  group: CourseMappingGroup,
  attributeKey: K,
  pendingChanges: CourseMappingPendingChanges,
  courseId: string
): CourseMappingPendingAttributeValue<K> | null => {
  const pendingValue = pendingChanges[courseId]?.[attributeKey];
  if (pendingValue !== undefined) {
    return pendingValue as CourseMappingPendingAttributeValue<K>;
  }

  return getOriginalAttributeValue(group, attributeKey);
};

export const getEffectiveCheckboxValue = (
  group: CourseMappingGroup,
  attributeKey: CourseMappingCheckboxAttributeKey,
  pendingChanges: CourseMappingPendingChanges,
  courseId: string
): boolean => Boolean(getEffectiveAttributeValue(group, attributeKey, pendingChanges, courseId));

const applyPendingAttributeToMappingAttributes = <K extends CourseMappingPendingAttributeKey>(
  attributes: CourseMappingAttributes,
  attributeKey: K,
  value: CourseMappingPendingAttributeValue<K>
): CourseMappingAttributes => {
  switch (attributeKey) {
    case 'subject_area':
      return { ...attributes, subject_area: { ...attributes.subject_area, name: value as string } };
    case 'subject_area_subcategory':
      return { ...attributes, subject_area: { ...attributes.subject_area, subcategory: value as string } };
    case 'language_category':
      return { ...attributes, language_category: value as string };
    case 'language_level':
      return { ...attributes, language_level: value as number };
    case 'number_of_credits':
      return { ...attributes, number_of_credits: value as number | null };
    default:
      return { ...attributes, [attributeKey]: value };
  }
};

const applyAttributeChangeForCourse = <K extends CourseMappingPendingAttributeKey>(
  pendingChanges: CourseMappingPendingChanges,
  sourceGroup: CourseMappingGroup,
  courseId: string,
  attributeKey: K,
  nextValue: CourseMappingPendingAttributeValue<K> | null
): CourseMappingPendingChanges => {
  const originalValue = getOriginalAttributeValue(sourceGroup, attributeKey);
  const courseChanges: CourseAttributesUpdate = { ...(pendingChanges[courseId] ?? {}) };

  if (valuesAreEqual(nextValue, originalValue)) {
    delete courseChanges[attributeKey];
  } else if (nextValue === null) {
    courseChanges[attributeKey] = null;
  } else {
    courseChanges[attributeKey] = nextValue;
  }

  const nextPendingChanges = { ...pendingChanges };

  if (Object.keys(courseChanges).length === 0) {
    delete nextPendingChanges[courseId];
  } else {
    nextPendingChanges[courseId] = courseChanges;
  }

  return nextPendingChanges;
};

// targetCourseIds is the variant's full course list for a collapsed (bulk) edit, or a single
// course id when editing an individual expanded row.
export const applyAttributeChange = <K extends CourseMappingPendingAttributeKey>(
  pendingChanges: CourseMappingPendingChanges,
  sourceGroup: CourseMappingGroup,
  targetCourseIds: string[],
  attributeKey: K,
  nextValue: CourseMappingPendingAttributeValue<K> | null
): CourseMappingPendingChanges =>
  targetCourseIds.reduce(
    (changes, courseId) => applyAttributeChangeForCourse(changes, sourceGroup, courseId, attributeKey, nextValue),
    pendingChanges
  );

export const applyCheckboxChange = (
  pendingChanges: CourseMappingPendingChanges,
  sourceGroup: CourseMappingGroup,
  targetCourseIds: string[],
  attributeKey: CourseMappingCheckboxAttributeKey,
  nextValue: boolean
): CourseMappingPendingChanges =>
  applyAttributeChange(pendingChanges, sourceGroup, targetCourseIds, attributeKey, nextValue);

const SUBJECT_AREA_DEPENDENT_ATTRIBUTES: CourseMappingDropdownAttributeKey[] = [
  'subject_area_subcategory',
  'language_category',
  'language_level',
];

const enrichAttributesForSave = (
  group: CourseMappingGroup,
  attributes: CourseAttributesUpdate
): CourseAttributesUpdate => {
  // The backend needs the subject area to resolve any dependent field, whether it is being set
  // or cleared. If a dependent changed without an accompanying subject_area change, re-attach the
  // course's current subject area so the payload stays unambiguous.
  const hasDependentChange = SUBJECT_AREA_DEPENDENT_ATTRIBUTES.some((key) => attributes[key] !== undefined);

  if (!hasDependentChange || attributes.subject_area !== undefined) {
    return attributes;
  }

  const subjectArea = getOriginalAttributeValue(group, 'subject_area');
  if (subjectArea == null) {
    return attributes;
  }

  return {
    ...attributes,
    subject_area: subjectArea,
  };
};

const applySubjectAreaChangeForCourse = (
  pendingChanges: CourseMappingPendingChanges,
  sourceGroup: CourseMappingGroup,
  courseId: string,
  nextValue: string | null
): CourseMappingPendingChanges => {
  const currentValue = getEffectiveAttributeValue(sourceGroup, 'subject_area', pendingChanges, courseId);

  if (valuesAreEqual(nextValue, currentValue)) {
    return pendingChanges;
  }

  let nextPendingChanges = applyAttributeChangeForCourse(
    pendingChanges,
    sourceGroup,
    courseId,
    'subject_area',
    nextValue
  );

  SUBJECT_AREA_DEPENDENT_ATTRIBUTES.forEach((attributeKey) => {
    nextPendingChanges = applyAttributeChangeForCourse(nextPendingChanges, sourceGroup, courseId, attributeKey, null);
  });

  return nextPendingChanges;
};

export const applySubjectAreaChange = (
  pendingChanges: CourseMappingPendingChanges,
  sourceGroup: CourseMappingGroup,
  targetCourseIds: string[],
  nextValue: string | null
): CourseMappingPendingChanges =>
  targetCourseIds.reduce(
    (changes, courseId) => applySubjectAreaChangeForCourse(changes, sourceGroup, courseId, nextValue),
    pendingChanges
  );

export const hasPendingChanges = (pendingChanges: CourseMappingPendingChanges): boolean =>
  Object.keys(pendingChanges).length > 0;

export const buildSavePayload = (
  groups: CourseMappingGroup[],
  pendingChanges: CourseMappingPendingChanges
): UpdateCourseAttributesRequest[] => {
  const groupByCourseId = new Map<string, CourseMappingGroup>();
  groups.forEach((group) => group.courseIds.forEach((courseId) => groupByCourseId.set(courseId, group)));

  return Object.entries(pendingChanges).flatMap(([courseId, attributes]) => {
    const group = groupByCourseId.get(courseId);
    if (!group || !attributes || Object.keys(attributes).length === 0) {
      return [];
    }

    return [
      {
        course_ids: [courseId],
        attributes: enrichAttributesForSave(group, attributes),
      },
    ];
  });
};

export const mergeSavePayload = (payload: UpdateCourseAttributesRequest[]): UpdateCourseAttributesRequest[] => {
  const mergedByAttributes = new Map<string, UpdateCourseAttributesRequest>();

  payload.forEach((entry) => {
    const attributesKey = JSON.stringify(entry.attributes);
    const existing = mergedByAttributes.get(attributesKey);

    if (existing) {
      existing.course_ids = Array.from(new Set([...existing.course_ids, ...entry.course_ids]));
      return;
    }

    mergedByAttributes.set(attributesKey, {
      course_ids: [...entry.course_ids],
      attributes: entry.attributes,
    });
  });

  return Array.from(mergedByAttributes.values());
};

// Per-row effective attributes: the variant's shared attributes with this course's pending edits
// layered on top. Sibling rows can now differ, so this is computed per course rather than per group.
export const getEffectiveRowAttributes = (
  group: CourseMappingGroup,
  courseId: string,
  pendingChanges: CourseMappingPendingChanges
): CourseMappingAttributes => {
  const courseChanges = pendingChanges[courseId];
  if (!courseChanges) {
    return group.attributes;
  }

  return Object.entries(courseChanges).reduce<CourseMappingAttributes>(
    (currentAttributes, [key, value]) => {
      if (value === undefined) {
        return currentAttributes;
      }

      return applyPendingAttributeToMappingAttributes(
        currentAttributes,
        key as CourseMappingPendingAttributeKey,
        value as CourseMappingPendingAttributeValue<CourseMappingPendingAttributeKey>
      );
    },
    { ...group.attributes }
  );
};
